import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  getAuthToken,
  AuthRequestBody,
  AuthResponse,
  logout as logoutAPI,
  getTwilioAuthToken,
  TwilioAuthResponse,
  verifyToken as verifyTokenAPI,
  VerifyTokenResponse,
  VerifyTokenResponseData,
} from '@services/api'
import { refreshAccessTokenViaCookie } from '@services/api/axiosConfig'
import { logger } from '@utils/safeLogger'
import { clearLocalStorageKeepingTheme } from '@utils/theme.util'
import { updateSelectedMessage } from './chat.action'
import type { ThunkDispatch } from '@reduxjs/toolkit'
import type { AnyAction } from 'redux'
import {
  disconnectCallsSocket,
  disconnectMessagingSockets,
} from '@services/websocket/socket'
import type { Device } from '@twilio/voice-sdk'
import { destroyTwilioVoiceDevice } from '@utils/device.twilio'
import {
  setCurrentCall,
  setDevice,
  setHasActiveCall,
} from '@store/reducers/calls.reducer'
import { setCallerInfo } from './calls.action'
import { RootState } from '@store/index'
import { clearAccessToken } from '@services/auth/accessTokenStore'

function teardownVoiceClientSession(
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
  getState: () => unknown,
): void {
  const device = (getState() as { calls: { device: Device | null } }).calls
    .device
  destroyTwilioVoiceDevice(device)
  dispatch(setDevice(null))
  dispatch(setCurrentCall(null))
  dispatch(setHasActiveCall(false))
  void dispatch(setCallerInfo(null))
}

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: AuthRequestBody, { rejectWithValue }) => {
    try {
      const response: AuthResponse = await getAuthToken(
        credentials.username,
        credentials.password,
        credentials.scope,
      )
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      return response.data // access_token (+ optional refresh_token for non-browser clients)
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  },
)

export const logout = createAsyncThunk<
  boolean,
  boolean | undefined,
  { rejectValue: string }
>(
  'auth/logout',
  async (expiredRefreshToken, { dispatch, getState, rejectWithValue }) => {
    try {
      const data = await logoutAPI()

      if (data.statusCode !== 200) {
        throw new Error('Authentication failed')
      }

      teardownVoiceClientSession(dispatch, getState)
      disconnectCallsSocket()
      disconnectMessagingSockets()

      clearAccessToken()
      // Remove local storage and non-HttpOnly cookies upon logout
      clearLocalStorageKeepingTheme()
      document.cookie.split(';').forEach((cookie) => {
        document.cookie = cookie
          .replace(/^\s*/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
      })

      dispatch(updateSelectedMessage())

      return expiredRefreshToken === undefined ? false : expiredRefreshToken
    } catch (e: any) {
      logger.error('Error during logout', e)
      // Still clear in-memory token on logout failure
      clearAccessToken()
      clearLocalStorageKeepingTheme()
      return rejectWithValue(e.message || 'Error during authentication')
    }
  },
)

export const createTwilioToken = createAsyncThunk(
  'api/auth/twilio-token',
  async (_, { rejectWithValue, getState }) => {
    const state = getState() as RootState
    const clinicId = state.clinic.selectedClinic?.clinic_id
    if (!clinicId) {
      return rejectWithValue('Clinic id is required to generate twilio token')
    }
    try {
      localStorage.removeItem('prev_call_id')
      const response: TwilioAuthResponse = await getTwilioAuthToken(clinicId)
      if (response.statusCode !== 200) {
        logger.error('Error occurred during twilio token generation')
        return rejectWithValue(response.message)
      }
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.message)
    }
  },
)

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const response: VerifyTokenResponse = await verifyTokenAPI()
      if (response.statusCode !== 200) {
        teardownVoiceClientSession(dispatch, getState)
        clearAccessToken()
        return rejectWithValue(response.message || 'Token verification failed')
      }
      return response.data
    } catch (error: any) {
      teardownVoiceClientSession(dispatch, getState)
      clearAccessToken()
      return rejectWithValue(error.message || 'Token verification failed')
    }
  },
)

/**
 * Boot-time session restore: use HttpOnly refresh cookie to obtain a new
 * access token, then verify it. Survives page refresh without localStorage JWTs.
 */
export const restoreSession = createAsyncThunk<
  VerifyTokenResponseData,
  void,
  { rejectValue: string }
>('auth/restoreSession', async (_, { rejectWithValue, dispatch, getState }) => {
  try {
    await refreshAccessTokenViaCookie()
    const response: VerifyTokenResponse = await verifyTokenAPI()
    if (response.statusCode !== 200) {
      teardownVoiceClientSession(dispatch, getState)
      clearAccessToken()
      return rejectWithValue(response.message || 'Token verification failed')
    }
    return response.data
  } catch (error: any) {
    teardownVoiceClientSession(dispatch, getState)
    clearAccessToken()
    return rejectWithValue(error.message || 'Session restore failed')
  }
})
